import FieldExplorer from 'components/field-explorer'
import FieldForm from 'components/field-form'
import CropYearPick from 'components/crop-year-pick'
import { Header as SideMenuHeader } from 'components/side-menu'
import SearchField from 'components/search-field'
import { fieldFetched, setCrop } from 'actions/field'
import { assignSelectedClient } from 'actions/client'
import { assignSelectedFarms } from 'actions/farm'
import { showModal } from 'actions/app'
import { isNil, get, isEmpty } from 'es-toolkit/compat'
import { fetchFields, setYear } from 'api/field'
import { reprocessHarvest, findHarvestsByFieldYearCrop } from 'api/harvest'
import { harvestFetched } from 'actions/harvest'
import SetHeader from 'components/harvest/set-header'
import OverrideCropType from 'components/harvest/override-crop-type'
import ChangeTare from 'components/harvest/set-tare'
import EditCropCalibration from 'components/harvest/edit-crop-calibration'
import ChangeTotalYield from 'components/harvest/change-total-yield'
import ChangeTestWeight from 'components/harvest/change-test-weight'
import ChangeTimeDelay from 'components/harvest/change-time-delay'
import ChangeHarvestYear from 'components/harvest/change-harvest-year'
import { Box } from '@mui/material'
import { useCallback, useEffect, useState } from 'react'
import { closeModal } from 'actions/app'
import { useDispatch } from 'react-redux'

import { useSelector } from 'react-redux'

export default function MapFieldsPanel() {
  const dispatch = useDispatch()

  const fields = useSelector((state) => get(state, 'field.collection', []))
  const crops = useSelector((state) => state.crop['cropsWithCropVarieties'])
  const field = useSelector((state) => get(state, 'field.singleton'))
  const fieldFeatures = useSelector((state) => get(state, 'field.features'))
  const harvests = useSelector((state) => state.harvest.collection)
  const initialCrop = useSelector((state) => get(state, 'field.crop'))
  const initialYear = useSelector((state) => get(state, 'field.year'))

  const selectedClient = useSelector((state) => get(state, 'client.singleton'))
  const farms = useSelector((state) => get(state, 'farm.selectedFarms'))
  const [crop, setSelectedCrop] = useState(initialCrop)
  const [year, setSelectedYear] = useState(initialYear)

  const [hideEmptyFields, setHideEmptyFields] = useState(false)
  const [filteredFields, setFields] = useState(fields)
  const [filterString, setFilterString] = useState('')
  const harvestYears = useSelector((state) => state.harvest.years)

  const cffEnabled = useSelector(
    (state) => state.account.singleton.prefs?.cff?.enabled ?? false,
  )

  useEffect(() => {
    setHideEmptyFields(crop !== 'all')
  }, [crop])

  const onCreateField = useCallback(() => {
    dispatch(showModal(<FieldForm />))
  }, [])

  const findFieldById = useCallback(
    (selectedField) => {
      dispatch(fieldFetched(selectedField))
      if (year && selectedField) {
        dispatch(findHarvestsByFieldYearCrop(selectedField.id, year, crop))
      }
      dispatch(harvestFetched({}))
    },
    [fields, year, crop],
  )

  const setSelectedClient = useCallback((client) => {
    dispatch(assignSelectedClient(client))
  }, [])

  const setSelectedFarms = useCallback(
    (farms) => {
      if (farms.length) {
        dispatch(assignSelectedFarms(farms))
      } else {
        dispatch(assignSelectedFarms([]))
      }
    },
    [year, crop],
  )

  const onSeasonChange = useCallback(
    (season) => {
      const [_year, _crop] = season.split('_')
      if (_year !== year && _year !== undefined && _year !== '') {
        dispatch(setYear(parseInt(_year)))
        setSelectedYear(_year)
      }
      if (_crop && crop != 'undefined') {
        setSelectedCrop(_crop)
        dispatch(setCrop(_crop))
      }
    },
    [year, crop],
  )

  useEffect(() => {
    if (year && crop) {
      if (cffEnabled) {
        if (farms?.length) {
          let farmIds = farms?.reduce((result, a) => {
            if (!isNil(a?.id)) {
              result.push(a.id)
            }
            return result
          }, [])
          dispatch(fetchFields(year, crop, selectedClient?.id, farmIds))
        } else if (selectedClient) {
          dispatch(fetchFields(year, crop, selectedClient?.id))
        } else {
          dispatch(fetchFields(year, crop))
        }
      } else {
        dispatch(fetchFields(year, crop))
      }
    }
  }, [year, crop, selectedClient, farms, cffEnabled])

  useEffect(() => {
    if (fields) {
      if (filterString) {
        setFields(
          fields.filter(
            (field) =>
              field.name &&
              field.name.toLowerCase().includes(filterString.toLowerCase()),
          ),
        )
      } else {
        setFields(fields)
      }
    }
  }, [fields, filterString])

  const onChangeTY = useCallback(() => {
    dispatch(showModal(<ChangeTotalYield />))
  }, [])

  const changeTestWeight = useCallback(() => {
    dispatch(showModal(<ChangeTestWeight />))
  }, [])

  const onSetHeader = useCallback(() => {
    dispatch(showModal(<SetHeader />))
  }, [])

  const onEditSensorCal = useCallback(() => {
    // setUpdateTare(true)
    dispatch(showModal(<ChangeTare />))
  }, [])

  const onChangeCropCal = useCallback(() => {
    // setChangeCropCal(true)
    dispatch(showModal(<EditCropCalibration />))
  })

  const onChangeTimeDelay = useCallback(() => {
    dispatch(showModal(<ChangeTimeDelay />))
  })

  const onChangeHarvestYear = useCallback(() => {
    dispatch(showModal(<ChangeHarvestYear />))
  })

  const onCropTypeOverride = useCallback(() => {
    dispatch(showModal(<OverrideCropType crops={crops} />))
  }, [crops])

  const onSearchField = useCallback((name) => {
    setFilterString(name)
  })

  const onRefreshHarvest = useCallback((harvest) => {
    dispatch(reprocessHarvest(harvest.id, harvest.tenantId))
    dispatch(closeModal())
  })

  useEffect(() => {
    if (field) dispatch(findHarvestsByFieldYearCrop(field.id, year, crop))
  }, [field, year, crop])

  const hasBoundary = useMemo(
    () => fieldFeatures !== undefined && !isEmpty(fieldFeatures),
    [fieldFeatures],
  )

  return (
    <Box
      sx={{
        height: '93vh',
        display: 'flex',
        flexDirection: 'column',
        padding: '1em 0 0 0',
        '& > div': {
          padding: '0 11px',
        },
      }}
    >
      <SideMenuHeader onCreateField={onCreateField} />
      <SearchField
        onSearch={onSearchField}
        name={filterString}
        sx={{ width: '100%', height: '30px' }}
      />
      <CropYearPick
        onSeasonChange={onSeasonChange}
        crops={crops}
        defaultCrop={crop}
        defaultYear={year}
        harvestYears={harvestYears}
        assignSelectedClient={setSelectedClient}
        assignSelectedFarms={setSelectedFarms}
        selectedFarms={farms}
        selectedClient={selectedClient}
      />
      <FieldExplorer
        fields={filteredFields}
        hideEmptyFields={hideEmptyFields}
        selectedField={field}
        harvests={harvests}
        onFieldSelected={findFieldById}
        hasBoundary={hasBoundary}
        onSetHeader={onSetHeader}
        onChangeTY={onChangeTY}
        onChangeTestWeight={changeTestWeight}
        onEditSensorCal={onEditSensorCal}
        onUpdateCropCal={onChangeCropCal}
        onChangeTimeDelay={onChangeTimeDelay}
        onCropTypeOverride={onCropTypeOverride}
        onRefreshHarvest={onRefreshHarvest}
        onChangeHarvestYear={onChangeHarvestYear}
      />
    </Box>
  )
}
